import { ChangeEvent, FC, useMemo, useRef } from 'react';
import classNames from 'classnames';

import MediaUploaderWrapper from 'application/components/MediaUploaderWrapper';
import VideoMediaUploaderWrapper from 'application/components/VideoMediaUploaderWrapper';
import { ACCEPT_AUDIO_FILETYPES, ACCEPT_VIDEO_FILETYPES } from 'application/pages/constants';
import usePromoState from 'application/hooks/usePromoState';
import { useAppSelector } from 'application/providers/StoreProvider';
import { usePlayerContext } from 'application/providers/PlayerProvider';
import CommonUtils from 'application/utils/CommonUtils';
import FileUtils from 'application/utils/FileUtils';

import { AdCampaignMediaTypeEnum } from '@shared/api/graphql/generated';
import { AdCampaignTypeEnum } from 'domain/media/types';

import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import { MediaUploaderItemType, MediaUploaderItemValue } from '@zvuk-b2b/react-uikit/ui/MediaUploader';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import { TIME_PATTERN } from '@zvuk-b2b/react-uikit/ui/TimeInput';

import ExactTimeMediaUploader from './ExactTimeMediaUploader';
import SortableAudioUploader from './SortableAudioUploader';
import { selectCampaignType } from '../../../slices/createAdCampaign/selectors';
import styles from './index.module.scss';
import { TEXTS, UPLOAD_BUTTON_TEXTS } from './texts';

export type AdCampaignStepperMediaProps = {
  mediaType?: AdCampaignMediaTypeEnum;
  onBack?: () => void;
  onNext?: () => void;
  close?: () => void;
  promoState: ReturnType<typeof usePromoState>;
  timeById: Record<string, string>;
  onTimeChange: (id: string, time: string) => void;
  onTimeRemove: (id: string) => void;
};

const AdCampaignStepperMedia: FC<AdCampaignStepperMediaProps> = ({
  mediaType = AdCampaignMediaTypeEnum.Audio,
  onBack,
  onNext,
  close,
  promoState,
  timeById,
  onTimeChange,
  onTimeRemove,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const campaignType = useAppSelector(selectCampaignType);
  const player = usePlayerContext();
  const { data, upload, remove, retry, reorder } = promoState;

  const isExactTime = campaignType === AdCampaignTypeEnum.ExactTime;
  const isInstant = campaignType === AdCampaignTypeEnum.Instant;
  const isDraggable = !isInstant;
  const accept = mediaType === AdCampaignMediaTypeEnum.Audio ? ACCEPT_AUDIO_FILETYPES : ACCEPT_VIDEO_FILETYPES;
  const acceptValue = Object.values(accept).flat().join(',');
  const uploadButtonText = UPLOAD_BUTTON_TEXTS[mediaType];
  const uploadDescription = TEXTS.MEDIA_UPLOAD_DESC(CommonUtils.convertObjectValuesToString(accept));
  const items = isDraggable ? data.rawStatuses : data.statuses;
  const mediaUploadItems = useMemo<MediaUploaderItemType[]>(
    () =>
      items.map((item) => ({
        label: item.file?.name || item.title || '',
        isLoading: item.isLoading,
        error: item.error,
        value: {
          id: item.id || '',
          filename: item.filename || '',
          url: FileUtils.getMediafileUrl(item.sourcefiles) || '',
          previewUrl: item.previewUrl || '',
          duration: item.duration,
          extension: item.sourcefiles ? item?.sourcefiles[0]?.extension : undefined,
        },
      })),
    [items]
  );

  const handleUploadClick = () => {
    inputRef.current?.click();
  };

  const handleFilesSelected = (files: File[]) => {
    upload(files, mediaType);
  };

  const handleFilesChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    if (files.length > 0) {
      handleFilesSelected(files);
    }

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleRemoveClick = (id: string) => {
    if (player.fileUuid === id) {
      player.stop?.();
    }

    remove(id);
    onTimeRemove(id);
  };

  const handleRetryClick = (id: string) => {
    retry(id, mediaType);
  };

  const handleRemoveMedia = (value: MediaUploaderItemValue) => {
    handleRemoveClick(value.id);
  };

  const handleRetryMedia = (value: MediaUploaderItemValue) => {
    handleRetryClick(value.id);
  };

  const handlePlayClick = (id: string, filename: string, url: string) => {
    player.play?.({ fileName: filename, fileUuid: id, fileUrl: url });
  };

  const handleTimeBlur = (id: string) => {
    const value = timeById[id];
    if (value !== undefined && !TIME_PATTERN.test(value)) {
      onTimeChange(id, '');
    }
  };

  const isUploadDisabled = isInstant && items.length > 0;

  const isNextDisabled =
    items.length === 0 ||
    items.some((item) => item.isLoading || item.error || !item.promoId) ||
    (isExactTime && items.some((item) => item.id && !TIME_PATTERN.test(timeById[item.id] ?? '')));

  return (
    <ModalScreen
      title={TEXTS.TITLE}
      titlePlacement="left"
      onClose={close}
      containerClassName={styles.container}
      titleClassName={styles.title}
      contentClassName={styles.content}
      containerActionButtonsClassName={styles.actionButtons}
      actionButtons={[
        <Button
          key="button_ad_campaign_stepper_media_next"
          elementId="button_ad_campaign_stepper_media_next"
          type={ButtonType.PRIMARY}
          isDisabled={isNextDisabled}
          onClick={onNext}>
          {TEXTS.NEXT}
        </Button>,
        <Button
          key="button_ad_campaign_stepper_media_back"
          elementId="button_ad_campaign_stepper_media_back"
          type={ButtonType.SECONDARY}
          onClick={onBack}>
          {TEXTS.BACK}
        </Button>,
      ]}>
      <div className={styles.root}>
        {!isExactTime && mediaType === AdCampaignMediaTypeEnum.Audio && isDraggable && (
          <SortableAudioUploader
            uploadButtonText={uploadButtonText}
            uploadDescription={uploadDescription}
            acceptValue={acceptValue}
            inputRef={inputRef}
            items={items}
            player={player}
            onUploadClick={handleUploadClick}
            onFilesChange={handleFilesChange}
            onPlayClick={handlePlayClick}
            onRemoveClick={handleRemoveClick}
            onRetryClick={handleRetryClick}
            onReorder={reorder}
          />
        )}

        {!isExactTime && mediaType === AdCampaignMediaTypeEnum.Audio && !isDraggable && (
          <MediaUploaderWrapper
            elementId="media_uploader_ad_campaign_stepper"
            accept={ACCEPT_AUDIO_FILETYPES}
            items={mediaUploadItems}
            isLoading={false}
            buttonTitle={uploadButtonText}
            description={uploadDescription}
            uploaderHintText={TEXTS.INSTANT_UPLOAD_HINT}
            isUploadButtonDisabled={isUploadDisabled}
            multiple={false}
            onDrop={handleFilesSelected}
            onRemove={handleRemoveMedia}
            onRetry={handleRetryMedia}
            mediaUploaderClassname={styles.mediaUploader}
          />
        )}

        {!isExactTime && mediaType === AdCampaignMediaTypeEnum.Video && (
          <VideoMediaUploaderWrapper
            elementId="media_uploader_ad_campaign_stepper"
            accept={ACCEPT_VIDEO_FILETYPES}
            items={mediaUploadItems}
            isLoading={false}
            buttonTitle={uploadButtonText}
            description={uploadDescription}
            onDrop={handleFilesSelected}
            onRemove={handleRemoveMedia}
            onRetry={handleRetryMedia}
            mediaUploaderClassname={classNames(
              styles.mediaUploader,
              mediaUploadItems.length === 0 && styles.mediaUploaderEmpty
            )}
          />
        )}

        {isExactTime && (
          <ExactTimeMediaUploader
            acceptValue={acceptValue}
            uploadButtonText={uploadButtonText}
            uploadDescription={uploadDescription}
            inputRef={inputRef}
            items={items}
            timeById={timeById}
            player={player}
            onUploadClick={handleUploadClick}
            onFilesChange={handleFilesChange}
            onTimeChange={onTimeChange}
            onTimeBlur={handleTimeBlur}
            onPlayClick={handlePlayClick}
            onRemoveClick={handleRemoveClick}
            onRetryClick={handleRetryClick}
            onReorder={reorder}
          />
        )}
      </div>
    </ModalScreen>
  );
};

export default AdCampaignStepperMedia;
